import { useRef, useState, type ChangeEvent } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useUpdateProfile, useUploadAvatar } from '../../api/hooks';
import { completeOnboarding } from '../../api/auth';
import { Avatar } from '../../components/Avatar';
import { BlueHeader } from '../../components/ScreenShell';
import { CameraIcon } from '../../components/icons';
import { haptics } from '../../telegram';

/**
 * Первый вход нового игрока (гейт после оферты): выбрать ник, рассказать
 * «откуда узнали», по желанию загрузить аватар. Существующие игроки экран не
 * видят — им onboarding_completed_at проставила миграция 0030.
 */
export function OnboardingScreen() {
  const auth = useAuth();
  const updateProfile = useUpdateProfile();
  const uploadAvatar = useUploadAvatar();
  const fileRef = useRef<HTMLInputElement>(null);

  const [nickname, setNickname] = useState(auth.player?.displayName ?? '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pickPhoto = () => fileRef.current?.click();

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setError(null);
    try {
      const res = await uploadAvatar.mutateAsync(file);
      auth.patchPlayer({ photoUrl: res.photoUrl });
      haptics.success();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить фото');
      haptics.error();
    }
  };

  const finish = async () => {
    const name = nickname.trim();
    if (!name || busy) return;
    setBusy(true);
    setError(null);
    try {
      // Ник — через общий PATCH /me: там проверка уникальности и синк клубного ника.
      if (name !== auth.player?.displayName) {
        await updateProfile.mutateAsync({ displayName: name });
      }
      const profile = await completeOnboarding(note.trim() || null);
      auth.patchPlayer({ displayName: profile.displayName, photoUrl: profile.photoUrl });
      haptics.success();
      auth.markOnboarded(); // unmounts this screen
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить');
      haptics.error();
      setBusy(false);
    }
  };

  return (
    <div>
      <BlueHeader mascot="tr">
        <div className="bhead-title">Добро пожаловать!</div>
      </BlueHeader>

      <div className="content">
      <div className="screen-subtitle" style={{ marginTop: 4 }}>
        Расскажите немного о себе — так вас будут видеть игроки за столом.
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={onFile}
      />

      <div className="edit-avatar-wrap">
        <button
          className="edit-avatar"
          onClick={pickPhoto}
          aria-label="Загрузить фото"
          disabled={uploadAvatar.isPending}
        >
          <Avatar
            name={nickname || auth.player?.displayName || '?'}
            src={auth.player?.photoUrl ?? null}
            size={88}
            bordered
          />
          <span className="cam">
            <CameraIcon />
          </span>
          {uploadAvatar.isPending && <span className="edit-avatar-loading" aria-hidden="true" />}
        </button>
        <button className="edit-photo-btn" onClick={pickPhoto} disabled={uploadAvatar.isPending}>
          {uploadAvatar.isPending ? 'Загрузка…' : 'Загрузить фото'}
        </button>
      </div>

      <div className={`field ${nickname ? 'field--active' : ''}`}>
        <div className="field-label">Никнейм</div>
        <input
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          placeholder="Как вас называть за столом"
          maxLength={40}
        />
      </div>

      <div className={`field ${note ? 'field--active' : ''}`}>
        <div className="field-label">Откуда вы о нас узнали?</div>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Telegram, друзья, интернет…"
          maxLength={500}
        />
      </div>

      {error && <div className="field-error auth-error">{error}</div>}

      <button className="btn" disabled={!nickname.trim() || busy} onClick={finish}>
        {busy ? 'Сохраняем…' : 'Начать играть'}
      </button>
      <div style={{ height: 24 }} />
      </div>
    </div>
  );
}
